import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const WheelContainer = styled.div`
  position: relative;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  margin: 2rem auto;
`;

const WheelCanvas = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 4px solid ${props => props.borderColor};
  position: relative;
  overflow: hidden;
  transition: transform ${props => props.spinDuration}ms ease-out;
  transform: rotate(${props => props.rotation}deg);
  background: ${props => props.backgroundColor};
`;

const Segment = styled.div`
  position: absolute;
  width: 50%;
  height: 50%;
  transform-origin: 100% 100%;
  transform: rotate(${props => props.angle}deg) skewY(${props => props.skew}deg);
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpinButtonContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SpinButton = styled.button`
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  background: ${props => props.buttonColor};
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Arrow = styled.div`
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 15px solid ${props => props.color};
  margin-bottom: 10px;
`;

const Wheel = ({
  segments = [],
  predeterminedWinner = 0,
  spinDuration = 5000,
  size = 400,
  buttonColor = '#ff4757',
  arrowColor = '#2f3542',
  borderColor = '#2f3542',
  backgroundColor = 'transparent',
  disabled = false,
  onSpinComplete,
  dailySpinLimit = true,
  customSpinButton
}) => {
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [lastSpin, setLastSpin] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('lastSpin');
    }
    return null;
  });

  const segmentAngles = 360 / segments.length;
  const skew = 90 - segmentAngles;

  const canSpin = useCallback(() => {
    if (!dailySpinLimit) return true;
    if (!lastSpin) return true;
    const lastSpinDate = new Date(parseInt(lastSpin));
    const currentDate = new Date();
    return currentDate - lastSpinDate > 86400000; // 24 hours
  }, [lastSpin, dailySpinLimit]);

  const calculateRotation = useCallback(() => {
    const fullSpins = 5; // Add multiple full spins for dramatic effect
    const winnerAngle = 360 - predeterminedWinner * segmentAngles; // Calculate the angle to bring the winner to the top
    const currentRotation = rotation % 360; // Normalize current rotation within 0-360 degrees
    const offset = (winnerAngle - currentRotation + 360) % 360; // Adjust offset to align winner at the top
    return fullSpins * 360 + offset; // Include full spins for the final rotation
  }, [predeterminedWinner, segmentAngles, rotation]);
  
  
  

  const handleSpin = () => {
    // if ((!canSpin() && dailySpinLimit) || disabled || isSpinning) return;
    
    setIsSpinning(true);
    const newRotation = calculateRotation();
    setRotation(rotation + newRotation);

    if (typeof window !== 'undefined') {
      localStorage.setItem('lastSpin', Date.now().toString());
      setLastSpin(Date.now().toString());
    }

    setTimeout(() => {
      setIsSpinning(false);
      onSpinComplete?.(segments[predeterminedWinner]);
    }, spinDuration);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedSpin = localStorage.getItem('lastSpin');
      setLastSpin(storedSpin);
    }
  }, []);

  return (
    <WheelContainer size={size}>
      <WheelCanvas
        rotation={rotation}
        spinDuration={spinDuration}
        borderColor={borderColor}
        backgroundColor={backgroundColor}
      >
        {segments.map((segment, index) => (
          <Segment
            key={index}
            angle={index * segmentAngles}
            skew={skew}
            color={segment.color || `hsl(${(index * 360) / segments.length}, 70%, 65%)`}
          >
            {segment.content}
          </Segment>
        ))}
      </WheelCanvas>

      <SpinButtonContainer>
        <Arrow color={arrowColor} />
        <SpinButton
          onClick={handleSpin}
        //   disabled={(!canSpin() && dailySpinLimit) || disabled || isSpinning}
          buttonColor={buttonColor}
        >
          {customSpinButton || (isSpinning ? 'Spinning...' : 'Spin')}
        </SpinButton>
      </SpinButtonContainer>
    </WheelContainer>
  );
};

Wheel.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string,
      content: PropTypes.node.isRequired
    })
  ).isRequired,
  predeterminedWinner: PropTypes.number,
  spinDuration: PropTypes.number,
  size: PropTypes.number,
  buttonColor: PropTypes.string,
  arrowColor: PropTypes.string,
  borderColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  disabled: PropTypes.bool,
  onSpinComplete: PropTypes.func,
  dailySpinLimit: PropTypes.bool,
  customSpinButton: PropTypes.node
};

export default Wheel;
